import { db } from '../config/firebase';
import { doc, setDoc, increment, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { hasConsent } from './cookieConsentService';

/**
 * Track a page view
 * GATED: Only tracks if user has given analytics consent
 */
export async function trackPageView(page = 'home') {
  // Hard gate - no tracking without consent
  if (!hasConsent('analytics')) {
    return;
  }

  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const docId = `${today}_${page}`;
    const analyticsRef = doc(db, 'analytics', docId);

    await setDoc(analyticsRef, {
      date: today,
      page,
      views: increment(1),
      lastUpdated: new Date().toISOString()
    }, { merge: true });
  } catch (error) {
    // Silently fail - don't break the app for analytics
    console.error('Analytics tracking error:', error);
  }
}

/**
 * Track unique visitor (using localStorage to avoid duplicates)
 * GATED: Only tracks if user has given analytics consent
 */
export async function trackUniqueVisitor() {
  // Hard gate - no tracking without consent
  if (!hasConsent('analytics')) {
    return;
  }

  try {
    const today = new Date().toISOString().split('T')[0];
    const visitorKey = `qm_visitor_${today}`;

    // Check if already tracked today
    if (localStorage.getItem(visitorKey)) {
      return;
    }

    const docRef = doc(db, 'analytics', `visitors_${today}`);
    await setDoc(docRef, {
      date: today,
      type: 'visitors',
      count: increment(1),
      lastUpdated: new Date().toISOString()
    }, { merge: true });

    localStorage.setItem(visitorKey, 'true');
  } catch (error) {
    console.error('Visitor tracking error:', error);
  }
}

// Get analytics for admin dashboard
export async function getAnalyticsSummary() {
  try {
    const today = new Date();
    const analyticsRef = collection(db, 'analytics');

    // Get today's date string
    const todayStr = today.toISOString().split('T')[0];

    // Get data for last 30 days
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];

    // Query all analytics docs
    const snapshot = await getDocs(analyticsRef);

    let todayViews = 0;
    let todayVisitors = 0;
    let weekViews = 0;
    let weekVisitors = 0;
    let monthViews = 0;
    let monthVisitors = 0;
    const dailyData = [];

    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];

    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const docDate = data.date;

      if (!docDate) return;

      // Today's stats
      if (docDate === todayStr) {
        if (data.type === 'visitors') {
          todayVisitors = data.count || 0;
        } else {
          todayViews += data.views || 0;
        }
      }

      // This week's stats
      if (docDate >= sevenDaysAgoStr) {
        if (data.type === 'visitors') {
          weekVisitors += data.count || 0;
        } else {
          weekViews += data.views || 0;
        }
      }

      // This month's stats
      if (docDate >= thirtyDaysAgoStr) {
        if (data.type === 'visitors') {
          monthVisitors += data.count || 0;
        } else {
          monthViews += data.views || 0;
        }

        // Collect daily data for charts
        if (data.type !== 'visitors') {
          const existing = dailyData.find(d => d.date === docDate);
          if (existing) {
            existing.views += data.views || 0;
          } else {
            dailyData.push({ date: docDate, views: data.views || 0 });
          }
        }
      }
    });

    // Sort daily data by date
    dailyData.sort((a, b) => a.date.localeCompare(b.date));

    return {
      today: {
        views: todayViews,
        visitors: todayVisitors
      },
      week: {
        views: weekViews,
        visitors: weekVisitors
      },
      month: {
        views: monthViews,
        visitors: monthVisitors
      },
      daily: dailyData
    };
  } catch (error) {
    console.error('Error fetching analytics:', error);
    return {
      today: { views: 0, visitors: 0 },
      week: { views: 0, visitors: 0 },
      month: { views: 0, visitors: 0 },
      daily: []
    };
  }
}
